import { DialogPurchaseCredits } from "@/components/team/dialog-purchase-credits";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAdminMode } from "@/contexts/admin-mode-provider";
import {
  CREDITS_RETRIEVAL_COST,
  useCredits,
} from "@/contexts/credits-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useTranslation } from "@/hooks/useTranslation";
import { useState } from "react";

const DIALOG_KEY = "components.dataTableCompanyEmployees.phone.confirmDialog";

/**
 * Everything that must happen between "the user asked for a paid phone" and the
 * reveal actually firing: warn about the cost, or — when the balance is short —
 * offer to top up, or tell the user to ask an admin.
 *
 * Owned by one component and shared by every surface that can spend credits on a
 * phone (the company employees table and the chat contact card). Duplicating the
 * three-way choice per surface is how one of them ends up debiting without a
 * warning, or showing an inert "not enough credits" the user cannot act on.
 *
 * Open it by setting `open`; it decides on its own which of the three prompts
 * applies from the live balance and the user's role.
 */
export function PhoneRevealCreditsGate({
  open,
  onOpenChange,
  onConfirm,
  onConfirmWithoutCost,
  onCreditsPurchased,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The user accepted the cost — perform the paid reveal. */
  onConfirm: () => void;
  /**
   * Admin-mode escape hatch performing the reveal without debiting. Omit on
   * surfaces whose endpoint has no such parameter, and the button is not shown.
   */
  onConfirmWithoutCost?: () => void;
  onCreditsPurchased?: () => void;
}) {
  const { creditsBalance } = useCredits();
  // Read here rather than taken as a prop so both surfaces gate on the same
  // rule; only these roles can reach the purchase flow.
  const { isAdmin, isManager } = useTeams();
  const [showPurchase, setShowPurchase] = useState(false);

  const canAfford = creditsBalance >= CREDITS_RETRIEVAL_COST.PHONE;
  const canPurchase = isAdmin || isManager;
  const close = () => onOpenChange(false);

  return (
    <>
      <ConfirmDialog
        open={open && canAfford}
        onOpenChange={onOpenChange}
        onCancel={close}
        onConfirm={() => {
          close();
          onConfirm();
        }}
        {...(onConfirmWithoutCost && {
          onConfirmWithoutCost: () => {
            close();
            onConfirmWithoutCost();
          },
        })}
      />

      <InsufficientCreditsDialog
        open={open && !canAfford && canPurchase}
        onOpenChange={onOpenChange}
        onCancel={close}
        onPurchase={() => {
          close();
          setShowPurchase(true);
        }}
        {...(onConfirmWithoutCost && {
          onConfirmWithoutCost: () => {
            close();
            onConfirmWithoutCost();
          },
        })}
      />

      <ContactAdminDialog
        open={open && !canAfford && !canPurchase}
        onOpenChange={onOpenChange}
        onCancel={close}
        {...(onConfirmWithoutCost && {
          onConfirmWithoutCost: () => {
            close();
            onConfirmWithoutCost();
          },
        })}
      />

      <DialogPurchaseCredits
        open={showPurchase}
        onOpenChange={setShowPurchase}
        currentCredits={creditsBalance}
        {...(onCreditsPurchased && { onCreditsPurchased })}
      />
    </>
  );
}

/** "This costs N credits of your X — continue?" */
function ConfirmDialog({
  open,
  onOpenChange,
  onCancel,
  onConfirm,
  onConfirmWithoutCost,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
  onConfirm: () => void;
  onConfirmWithoutCost?: () => void;
}) {
  const { t } = useTranslation();
  const { isAdminMode } = useAdminMode();
  const { creditsBalance } = useCredits();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-full max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>{t(`${DIALOG_KEY}.title`)}</AlertDialogTitle>
          <AlertDialogDescription>
            {t(`${DIALOG_KEY}.description`, {
              cost: CREDITS_RETRIEVAL_COST.PHONE,
              currentCredits: creditsBalance,
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>
            {t("common.cancel")}
          </AlertDialogCancel>
          {isAdminMode && onConfirmWithoutCost && (
            <AlertDialogAction
              onClick={onConfirmWithoutCost}
              className="border-2 border-warning"
              variant="outline"
            >
              {t(`${DIALOG_KEY}.confirmWithoutCost`)}
            </AlertDialogAction>
          )}
          <AlertDialogAction onClick={onConfirm}>
            {t(`${DIALOG_KEY}.confirm`)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Balance too low, but this user may top up. */
function InsufficientCreditsDialog({
  open,
  onOpenChange,
  onCancel,
  onPurchase,
  onConfirmWithoutCost,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
  onPurchase: () => void;
  onConfirmWithoutCost?: () => void;
}) {
  const { t } = useTranslation();
  const { isAdminMode } = useAdminMode();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="w-full max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(`${DIALOG_KEY}.insufficientCredits.title`)}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(`${DIALOG_KEY}.insufficientCredits.description`)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>
            {t("common.cancel")}
          </AlertDialogCancel>
          {isAdminMode && onConfirmWithoutCost && (
            <AlertDialogAction
              onClick={onConfirmWithoutCost}
              className="border-2 border-warning"
              variant="outline"
            >
              {t(`${DIALOG_KEY}.confirmWithoutCost`)}
            </AlertDialogAction>
          )}
          <AlertDialogAction onClick={onPurchase}>
            {t(`${DIALOG_KEY}.insufficientCredits.confirm`)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Balance too low and this user cannot buy credits themselves. */
function ContactAdminDialog({
  open,
  onOpenChange,
  onCancel,
  onConfirmWithoutCost,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
  onConfirmWithoutCost?: () => void;
}) {
  const { t } = useTranslation();
  const { isAdminMode } = useAdminMode();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(`${DIALOG_KEY}.contactAdmin.title`)}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(`${DIALOG_KEY}.contactAdmin.description`)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>
            {t("common.cancel")}
          </AlertDialogCancel>
          {/* An admin keeps the escape hatch even without a team purchasing
              role: being short on credits must never block them. */}
          {isAdminMode && onConfirmWithoutCost && (
            <AlertDialogAction
              onClick={onConfirmWithoutCost}
              className="border-2 border-warning"
              variant="outline"
            >
              {t(`${DIALOG_KEY}.confirmWithoutCost`)}
            </AlertDialogAction>
          )}
          <AlertDialogAction onClick={onCancel}>
            {t(`${DIALOG_KEY}.contactAdmin.confirm`)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
